import { lazy, Suspense } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { AuthProvider } from "./contexts/AuthContext"
import { Login } from "./pages/Login"
import { LoginOtp } from "./pages/LoginOtp"
import { ForgotPassword } from "./pages/ForgotPassword"
import { Support } from "./pages/Support"
import { Orders } from "./pages/Orders"
import { QueryProvider } from "./providers/QueryProvider"
import { MemberDetails } from "./pages/MemberDetails"
import { Members } from "./pages/Members"
import { ChargeMember } from "./pages/ChargeMember"
import { Toaster } from "./components/ui/toaster"
import { GenerateReport } from "./pages/GenerateReport"
import { Team } from "./pages/Team"
import { Policies } from "./pages/Policies"
import { PrivacyPolicy } from "./pages/PrivacyPolicy"
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ManagerProtectedRoute } from "./components/core/protected/ManagerProtectedRoute"
import { ViewerProtectedRoute } from "./components/core/protected/ViewerProtectedRoute"
import { withRoleProtection } from "./hocs/withRoleProtection"
import { useAuth } from "./contexts/AuthContext"
import { useOnlineStatus } from "./hooks/useOnlineStatus"
import { OfflineOverlay } from "./components/blocks/OfflineOverlay"
import { ProtectedRoute } from "./components/core/protected/ProtectedRoute"
import { DashboardProtectedRoute } from "./dashboard/components/DashboardProtectedRoute"
import { DashboardPageRoute } from "./dashboard/components/DashboardPageRoute"
import { DASHBOARD_PAGES } from "./dashboard/visibility"
import { DASHBOARD_HOME_ROUTE, getHomeRoute } from "./common/routes.common"
import { IS_HUB_PREVIEW } from "./preview/hubPreview"
import { HUB_PREVIEW_PATH } from "./preview/hubPreviewProtocol"
import { HubPreviewRouteSync } from "./preview/HubPreviewRouteSync"

// The dashboard is a separate desktop experience; keep it out of the mobile app bundle.
const dashboardLayoutImport = () =>
  import("./dashboard/layout/DashboardLayout").then((m) => ({ default: m.DashboardLayout }))
const DashboardLayout = lazy(dashboardLayoutImport)
// The Hub preview always lands on the dashboard. Start the chunk during the handshake instead of after auth.
if (IS_HUB_PREVIEW) void dashboardLayoutImport()
const DashboardHome = lazy(() =>
  import("./dashboard/pages/DashboardHome").then((m) => ({ default: m.DashboardHome }))
)
const WebsiteLeads = lazy(() =>
  import("./dashboard/pages/WebsiteLeads").then((m) => ({ default: m.WebsiteLeads }))
)
const DashboardFunding = lazy(() =>
  import("./dashboard/pages/DashboardFunding").then((m) => ({ default: m.DashboardFunding }))
)
const GuestFlow = lazy(() =>
  import("./dashboard/pages/GuestFlow").then((m) => ({ default: m.GuestFlow }))
)
const DashboardReports = lazy(() =>
  import("./dashboard/pages/DashboardReports").then((m) => ({ default: m.DashboardReports }))
)

// Placeholder components for routes
const Transactions = () => <div>Transactions Page</div>
const Customers = () => <div>Customers Page</div>
const Settings = () => <div>Settings Page</div>

// Root redirect component that conditionally redirects based on auth state
const RootRedirect = () => {
  const { user } = useAuth();
  if (IS_HUB_PREVIEW) return <Navigate to={DASHBOARD_HOME_ROUTE} replace />;
  return user ? <Navigate to={getHomeRoute(user)} /> : <Navigate to="/login" />;
}

function App() {
  const { isOnline } = useOnlineStatus();

  return (
    <ErrorBoundary>
      <OfflineOverlay isOnline={isOnline} />
      <Router>
        <QueryProvider>
          <AuthProvider>
            {IS_HUB_PREVIEW && <HubPreviewRouteSync />}
            <Routes>
              {/* The Hub's merchant dashboard preview loads this in an iframe */}
              <Route path={HUB_PREVIEW_PATH} element={<RootRedirect />} />

              <Route path="/login" element={<Login />} />
              <Route path="/login/otp" element={<LoginOtp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Waiter Routes */}
              <Route
                element={withRoleProtection(ViewerProtectedRoute)}
              >
                <Route path="/orders" element={<Orders />} />
                <Route path="/policies" element={<Policies />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              </Route>

              {/* Manager Routes */}
              <Route
                element={withRoleProtection(ManagerProtectedRoute)}
              >
                <Route path="/transactions" element={<Transactions />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/support" element={<Support />} />
                <Route path="/team" element={<Team />} />                
                <Route path="/members" element={<Members />} />
                <Route path="/members/:memberId" element={<MemberDetails />} />
                <Route
                  path="/members/:memberId/charge"
                  element={<ChargeMember />}
                />
                <Route path="/reports" element={<GenerateReport />} />
              </Route>

              {/* Merchant dashboard: login required, then the dashboard access toggle */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardProtectedRoute>
                      <Suspense fallback={null}>
                        <DashboardLayout />
                      </Suspense>
                    </DashboardProtectedRoute>
                  </ProtectedRoute>
                }
              >
                <Route
                  index
                  element={
                    <DashboardPageRoute page={DASHBOARD_PAGES.overview}>
                      <DashboardHome />
                    </DashboardPageRoute>
                  }
                />

                <Route
                  path="website-leads"
                  element={
                    <DashboardPageRoute page={DASHBOARD_PAGES.websiteLeads}>
                      <WebsiteLeads />
                    </DashboardPageRoute>
                  }
                />

                <Route
                  path="funding"
                  element={
                    <DashboardPageRoute page={DASHBOARD_PAGES.funding}>
                      <DashboardFunding />
                    </DashboardPageRoute>
                  }
                />

                <Route
                  path="guest-flow"
                  element={
                    <DashboardPageRoute page={DASHBOARD_PAGES.guestFlow}>
                      <GuestFlow />
                    </DashboardPageRoute>
                  }
                />

                <Route
                  path="reports"
                  element={
                    <DashboardPageRoute page={DASHBOARD_PAGES.reports}>
                      <DashboardReports />
                    </DashboardPageRoute>
                  }
                />
              </Route>

              {/* Conditional root redirect */}
              <Route path="/" element={<RootRedirect />} />
            </Routes>
            <Toaster />
          </AuthProvider>
        </QueryProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App
